import cx from 'classnames';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import styles from '../../styles/modules/modal/Modal.module.scss';

import { useConfigStore } from '../../store/useConfigStore';

const ANIM_MS = 750;

const ModalRoot = () => {
	const {
		closeModal,
		modalProps,
		isModalOpen,
		modalComponent: ModalComponent,
	} = useConfigStore((s) => s);

	const [cachedComponent, setCachedComponent] = useState(null);
	const [cachedProps, setCachedProps] = useState(null);

	const [isMounted, setIsMounted] = useState(false);
	const [isVisible, setIsVisible] = useState(false);

	const closingTimerRef = useRef(null);

	useLayoutEffect(() => {
		if (!isModalOpen || !ModalComponent) return;

		if (closingTimerRef.current) clearTimeout(closingTimerRef.current);

		setIsVisible(false);

		setCachedComponent(() => ModalComponent);
		setCachedProps(modalProps || null);
		setIsMounted(true);

		const raf = requestAnimationFrame(() => setIsVisible(true));

		return () => cancelAnimationFrame(raf);
	}, [isModalOpen, ModalComponent, modalProps]);

	useEffect(() => {
		if (isModalOpen || !isMounted) return;

		setIsVisible(false);

		closingTimerRef.current = setTimeout(() => {
			setIsMounted(false);
			setCachedComponent(null);
			setCachedProps(null);
		}, ANIM_MS);

		return () => {
			if (closingTimerRef.current) clearTimeout(closingTimerRef.current);
		};
	}, [isModalOpen, isMounted]);

	useEffect(() => {
		if (!isMounted) return;

		const onKeyDown = (e) => {
			if (e.key === 'Escape') closeModal();
		};

		window.addEventListener('keydown', onKeyDown);

		return () => window.removeEventListener('keydown', onKeyDown);
	}, [isMounted, closeModal]);

	if (!isMounted || !cachedComponent) return null;

	const Payload = cachedComponent;

	return (
		<div
			className={cx(styles.modalOverlay, { [styles.isVisible]: isVisible })}
			onMouseDown={closeModal}
			role='presentation'
		>
			<div
				className={cx(styles.modalContainer, { [styles.isVisible]: isVisible })}
				onMouseDown={(e) => e.stopPropagation()}
				role='dialog'
				aria-modal='true'
			>
				<Payload onClose={closeModal} {...(cachedProps || {})} />
			</div>
		</div>
	);
};

export default ModalRoot;
